import React, { useState } from 'react';
import {
  getAuth,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import '../../../../../Stylesheets/Profile/ChangePasswordModal.css';
import AlertBox from '../../../../../Components/Common/AlertBox';
import { useDispatch } from 'react-redux';
import { showFeedback } from '../../../../../Redux/UI/uiFeedbackSlice';

const ChangePasswordModal = ({ onClose }) => {
  const dispatch = useDispatch();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // ✅ inline alert for the modal
  const [alertState, setAlertState] = useState({ type: 'error', message: '' });
  const setAlert = (message, type = 'error') => setAlertState({ type, message });
  const clearAlert = () => setAlertState({ type: 'error', message: '' });

  const getFriendlyError = (code) => {
    switch (code) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
        return 'Current password is incorrect.';
      case 'auth/weak-password':
        return 'New password should be at least 6 characters.';
      case 'auth/too-many-requests':
        return 'Too many attempts. Please try again later.';
      case 'auth/requires-recent-login':
        return 'For security, please sign in again and retry changing your password.';
      default:
        return 'Something went wrong. Please try again.';
    }
  };

  const handleChangePassword = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    clearAlert();

    if (!user) {
      setAlert('No user is currently logged in.', 'error');
      return;
    }

    if (!currentPassword || !newPassword || !confirmNewPassword) {
      setAlert('All fields are required.', 'error');
      return;
    }

    if (newPassword.length < 6) {
      setAlert('New password should be at least 6 characters.', 'error');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setAlert('New passwords do not match.', 'error');
      return;
    }

    if (currentPassword === newPassword) {
      setAlert('New password must be different from current password.', 'error');
      return;
    }

    try {
      setIsSaving(true);

      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);

      // ✅ inline success (optional)
      setAlert('Password updated successfully.', 'success');

      // ✅ global success (recommended)
      dispatch(
        showFeedback({
          type: 'success',
          title: 'Password updated',
          message: 'Your password has been changed successfully.',
        })
      );

      // close quickly after user sees it
      setTimeout(() => onClose(), 350);
    } catch (err) {
      console.error(err);
      setAlert(getFriendlyError(err.code), 'error');

      // optional: global error too (I would NOT do both; feels spammy)
      // dispatch(showFeedback({ type:'error', title:'Change failed', message: getFriendlyError(err.code) }));
    } finally {
      setIsSaving(false);
    }
  };

  const onInputChange = (setter) => (e) => {
    if (alertState.message) clearAlert();
    setter(e.target.value);
  };

  return (
    <div className="change-pass-modal-overlay">
      <div className="change-pass-modal-content">
        <h3>Change Password</h3>

        <AlertBox
          type={alertState.type}
          message={alertState.message}
          onClose={clearAlert}
        />

        <input
          type="password"
          placeholder="Current Password"
          value={currentPassword}
          onChange={onInputChange(setCurrentPassword)}
        />

        <input
          type="password"
          placeholder="New Password"
          value={newPassword}
          onChange={onInputChange(setNewPassword)}
        />

        <input
          type="password"
          placeholder="Confirm New Password"
          value={confirmNewPassword}
          onChange={onInputChange(setConfirmNewPassword)}
        />

        <div className="change-pass-modal-actions">
          <button onClick={handleChangePassword} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
          </button>

          <button
            onClick={onClose}
            className="change-pass-cancel-btn"
            disabled={isSaving}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordModal;
